"use client";

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";

import { defaultLocale, type Locale } from "@/lib/i18n";
import { normalizeMessageKey, t } from "@/content/i18n/messages";

const LocaleContext = createContext<Locale>(defaultLocale);

interface LocaleProviderProps {
  locale: Locale;
  children: ReactNode;
}

export function LocaleProvider({
  locale,
  children,
}: LocaleProviderProps) {
  return (
    <LocaleContext.Provider value={locale}>
      <LocalizedDocumentText locale={locale} />
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

export function useLocalizedText(text: string): string {
  return t(useLocale(), text);
}

const ignoredTextTags = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "IFRAME",
  "SVG",
  "CANVAS",
  "CODE",
  "PRE",
  "TEXTAREA",
]);

const translatableAttributes = [
  "alt",
  "aria-label",
  "placeholder",
  "title",
] as const;

function preserveOuterWhitespace(originalText: string, translatedText: string): string {
  const leadingWhitespace = originalText.match(/^\s*/)?.[0] ?? "";
  const trailingWhitespace = originalText.match(/\s*$/)?.[0] ?? "";

  return `${leadingWhitespace}${translatedText}${trailingWhitespace}`;
}

function shouldTranslateTextNode(node: Text): boolean {
  const parentElement = node.parentElement;

  return Boolean(
    parentElement &&
      !ignoredTextTags.has(parentElement.tagName) &&
      !parentElement.closest("[data-i18n-skip]") &&
      normalizeMessageKey(node.nodeValue ?? ""),
  );
}

function translateElementAttributes(element: Element, locale: Locale): void {
  translatableAttributes.forEach((attribute) => {
    const currentValue = element.getAttribute(attribute);

    if (!currentValue) {
      return;
    }

    const normalizedValue = normalizeMessageKey(currentValue);
    const translatedValue = t(locale, normalizedValue);

    if (translatedValue !== currentValue) {
      element.setAttribute(attribute, translatedValue);
    }
  });
}

function translateDocumentText(locale: Locale): void {
  if (locale === defaultLocale || typeof document === "undefined") {
    return;
  }

  const rootElement = document.body;
  const textWalker = document.createTreeWalker(
    rootElement,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        return shouldTranslateTextNode(node as Text)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    },
  );

  const textNodes: Text[] = [];
  let currentNode = textWalker.nextNode();

  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = textWalker.nextNode();
  }

  textNodes.forEach((node) => {
    const currentValue = node.nodeValue ?? "";
    const normalizedValue = normalizeMessageKey(currentValue);
    const translatedValue = t(locale, normalizedValue);

    if (translatedValue !== normalizedValue) {
      node.nodeValue = preserveOuterWhitespace(currentValue, translatedValue);
    }
  });

  rootElement
    .querySelectorAll(
      translatableAttributes.map((attribute) => `[${attribute}]`).join(","),
    )
    .forEach((element) => translateElementAttributes(element, locale));
}

function LocalizedDocumentText({ locale }: { locale: Locale }) {
  useEffect(() => {
    if (locale === defaultLocale) {
      return;
    }

    const translate = () => translateDocumentText(locale);
    const animationFrameId = window.requestAnimationFrame(translate);
    const observer = new MutationObserver(translate);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...translatableAttributes],
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [locale]);

  return null;
}
